import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import ScheduledCronJobCard from "./ScheduledCronJobCard";
import LaunchedTemplateReviewForm from "../../components/LaunchedTemplates/LaunchedTemplateReviewForm";
import { downloadMarketplaceTemplate } from "../MarketplaceTemplateActions";
import { toast } from "react-toastify";

// Automation - Explore flow:
// 1. Modules              -> GET /module-plans/module-plans/default-db
// 2. Categories            -> GET /automation-category/module/{moduleId}/default-db
// 3. Cron Job Types        -> GET /scheduled-cron-jobs/cron-job-type/category/{categoryId}
// 4. Launched Automations  -> GET /scheduled-cron-jobs/launched-automations
//      query: page, size, status, versionType, cronJobId
// 5. Browse-all (NEW)      -> GET /scheduled-cron-jobs/module/{moduleId}
//      query: page, size, status, versionType
//      data: [ { categoryId, categoryName, cronJobs: [ { cronJobId, cronJobName,
//              eventCode, scheduledCronJobs: [...], totalScheduledCronJobs, page, size, totalPages } ] } ]
//      Used for the default "browse everything" view with infinite scroll (page grows by
//      PAGE_SIZE_ALL each time the user scrolls near the bottom). Clicking any filter
//      (category / cron job type) switches back to the focused, single-selection flow
//      above, which still uses the existing id-based endpoints (#2/#3/#4) exactly as before.
// 6. Save                  -> POST /scheduled-cron-jobs/save (not wired yet)

const STATUS = "LAUNCHED"; // always sent, not user-selectable
const VERSION_TYPE_OPTIONS = ["NEW", "UPDATED"];
const PAGE_SIZE = 12; // 3 rows x 4 cards, used by the focused/filtered view
const PAGE_SIZE_ALL = 10; // used by the browse-all / infinite-scroll view

// The bulk endpoint names the cron job group "cronJobId"/"cronJobName", while the
// existing per-category endpoint names it "cronJobTypeId"/"cronJobTypeName" - fall
// back across both shapes so either source works.
const getCronJobTypeId = (type) =>
  type?.cronJobTypeId ?? type?.cronJobId ?? type?.id;
const getCronJobTypeName = (type) =>
  type?.cronJobTypeName ?? type?.cronJobName ?? type?.name;
const getCategoryId = (cat) => cat?.categoryId ?? cat?.id;
const getCategoryName = (cat) => cat?.categoryName ?? cat?.name;
// Main tab route that lists the person's own (downloaded) templates.
const MAIN_ROUTE = "/admin/automation";
const getTemplateCode = (template) =>
  template?.templateCode ??
  template?.notification?.templateCode ??
  template?.automation?.templateCode;
// Marketplace templates are always saved as a NEW copy, so the source
// record's ID is never sent along (a populated ID would mean "update").
const asNewCopy = (template) => {
  const { scheduledCronJobId, ...rest } = template;
  return { ...rest, alreadyUsed: false };
};

const AutomationExplore = ({ displayMode = "gallery" }) => {
  const { user, canAccessModule } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?moduleId=&categoryId=&cronJobTypeId= from an "Explore" link on the Main
  // tab, e.g. /admin/automation/explore?moduleId=1&categoryId=2&cronJobTypeId=3.
  // Read live (not just once) because the Explore route can already be
  // mounted when a new link is clicked - React Router re-renders it with new
  // params instead of remounting, so a one-time ref would miss the update.
  const targetModuleId = searchParams.get("moduleId");
  const targetCategoryId = searchParams.get("categoryId");
  const targetCronJobTypeId = searchParams.get("cronJobTypeId");

  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cronJobTypes, setCronJobTypes] = useState([]);

  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCronJobType, setSelectedCronJobType] = useState(null);

  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingCronJobTypes, setIsLoadingCronJobTypes] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  const [versionType, setVersionType] = useState("NEW");
  const [page, setPage] = useState(0);

  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [reviewItem, setReviewItem] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  // "all" = default browse-everything view (new bulk endpoint + infinite scroll)
  // "filtered" = the original single category/cron-job-type flow with Prev/Next paging
  const [mode, setMode] = useState("all");
  const [allCategories, setAllCategories] = useState([]);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [isLoadingMoreAll, setIsLoadingMoreAll] = useState(false);
  const [allPage, setAllPage] = useState(0);
  const [allHasMore, setAllHasMore] = useState(true);
  const sentinelRef = useRef(null);

  // Fetch the module list once on mount. Selecting a module (default or
  // deep-linked) is handled separately below, once modules are loaded.
  useEffect(() => {
    const fetchModules = async () => {
      try {
        setIsLoadingModules(true);
        setError(null);
        const { data } = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/module-plans/module-plans/default-db`,
        );
        setModules(Array.isArray(data) ? data : data?.data || []);
      } catch (err) {
        console.error("Failed to fetch modules", err);
        setError("Failed to load modules.");
        setModules([]);
      } finally {
        setIsLoadingModules(false);
      }
    };

    fetchModules();
  }, []);

  const handleModuleClick = async (mod) => {
    const moduleId = mod.moduleId ?? mod.id;

    setSelectedModule(mod);
    setSelectedCategory(null);
    setSelectedCronJobType(null);
    setCategories([]);
    setCronJobTypes([]);
    setTemplates([]);
    setPage(0);
    setMode("all");
    setAllCategories([]);
    setAllPage(0);
    setAllHasMore(true);

    try {
      setIsLoadingCategories(true);
      setError(null);
      const { data } = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/automation-category/module/${moduleId}/default-db`,
      );
      const fetchedCategories = Array.isArray(data) ? data : data?.data || [];
      setCategories(fetchedCategories);

      // Only auto-drill into a category if it belongs to the module we were
      // asked to land on - otherwise leftover params from a previous link
      // could pull the user into the wrong category on a manual module click.
      if (targetCategoryId && String(moduleId) === String(targetModuleId)) {
        const targetCategory = fetchedCategories.find(
          (cat) => String(getCategoryId(cat)) === String(targetCategoryId),
        );
        if (targetCategory) {
          handleCategoryClick(targetCategory);
        }
      }
    } catch (err) {
      console.error("Failed to fetch categories", err);
      setError("Failed to load categories.");
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Select the deep-linked module (or the first accessible one by default)
  // whenever the target changes - including when the Explore route was
  // already mounted and a new "Explore" link is clicked, since that only
  // changes the query string rather than remounting this component.
  useEffect(() => {
    if (isLoadingModules || modules.length === 0) return;

    const targetModule = targetModuleId
      ? modules.find(
          (mod) => String(mod.moduleId ?? mod.id) === String(targetModuleId),
        )
      : null;
    const firstAccessibleModule = modules.find((mod) =>
      canAccessModule(mod.moduleAccessId ?? mod.moduleAccessID),
    );
    const moduleToSelect = targetModule ?? firstAccessibleModule;

    if (moduleToSelect) handleModuleClick(moduleToSelect);
    // handleModuleClick intentionally stays out of dependencies to avoid
    // recreating the initial API request on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingModules,
    modules,
    targetModuleId,
    targetCategoryId,
    targetCronJobTypeId,
  ]);

  const handleCategoryClick = async (category) => {
    const categoryId = category.categoryId ?? category.id;

    setMode("filtered");
    setSelectedCategory(category);
    setSelectedCronJobType(null);
    setCronJobTypes([]);
    setTemplates([]);

    try {
      setIsLoadingCronJobTypes(true);
      setError(null);
      const { data } = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/scheduled-cron-jobs/cron-job-type/category/${categoryId}`,
      );
      const fetchedTypes = Array.isArray(data) ? data : data?.data || [];
      setCronJobTypes(fetchedTypes);

      // Same guard as above: only auto-select a cron job type if this
      // category is the one the deep link actually pointed at.
      if (
        targetCronJobTypeId &&
        String(categoryId) === String(targetCategoryId)
      ) {
        const targetType = fetchedTypes.find(
          (type) =>
            String(getCronJobTypeId(type)) === String(targetCronJobTypeId),
        );
        if (targetType) {
          handleCronJobTypeClick(targetType);
        }
      }
    } catch (err) {
      console.error("Failed to fetch cron job types", err);
      setError("Failed to load cron job types.");
      setCronJobTypes([]);
    } finally {
      setIsLoadingCronJobTypes(false);
    }
  };

  const handleCronJobTypeClick = (cronJobType) => {
    setMode("filtered");
    setSelectedCronJobType(cronJobType);
    setPage(0);
  };

  const handleBackToAll = () => {
    setMode("all");
    setSelectedCategory(null);
    setSelectedCronJobType(null);
    setCronJobTypes([]);
    setTemplates([]);
    setPage(0);
  };

  const performDownload = async (template) => {
    const templateId = template.scheduledCronJobId;
    try {
      setDownloadingId(templateId);
      await downloadMarketplaceTemplate({
        item: asNewCopy(template),
        type: "cron",
        user,
      });
      toast.success("Template downloaded successfully.");
    } catch (err) {
      console.error("Failed to download automation template", err);
      toast.error("Unable to download this template. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  // Card "Download" downloads straight away. "Customize & Download" opens the
  // customize popup; its Download button stays disabled until something is
  // changed.
  const handleTemplateAction = (template, action) => {
    if (action === "edit") setReviewItem(asNewCopy(template));
    else performDownload(template);
  };

  // "View used template": open the Main tab on the category / type this
  // template belongs to; Main highlights the copy with the same templateCode.
  const viewUsedTemplate = (template, category, type) => {
    const params = new URLSearchParams();
    // Module IDs differ between Explore (default-tenant modules) and Main
    // (module plans); moduleAccessId (e.g. "LEAVE") is the shared key.
    const moduleAccessId =
      selectedModule?.moduleAccessId ?? selectedModule?.moduleAccessID;
    const categoryId = getCategoryId(category ?? selectedCategory);
    const typeId = getCronJobTypeId(type ?? selectedCronJobType);
    const templateCode = getTemplateCode(template);

    if (moduleAccessId) params.set("moduleAccessId", moduleAccessId);
    if (categoryId) params.set("categoryId", categoryId);
    if (typeId) params.set("cronJobTypeId", typeId);
    if (templateCode) params.set("templateCode", templateCode);

    // IDs of the person's own copies of this template (what Main lists).
    const scheduledIds = [].concat(template?.tenantScheduledId ?? []);
    if (scheduledIds.length) params.set("scheduledIds", scheduledIds.join(","));

    // Also keep the deep link in sessionStorage: if a route redirect drops the
    // query string on the way to Main, Main can still pick it up from here.
    try {
      sessionStorage.setItem(
        "usedTemplateDeepLink",
        JSON.stringify({
          moduleAccessId: params.get("moduleAccessId"),
          categoryId: params.get("categoryId"),
          typeId: params.get("cronJobTypeId"),
          templateCode: params.get("templateCode"),
          scheduledIds: scheduledIds.map(String),
          ts: Date.now(),
        }),
      );
    } catch {
      /* storage unavailable: the URL params still work */
    }

    navigate(`${MAIN_ROUTE}?${params.toString()}`);
  };

  // Fetch launched automations whenever the selected cron job type or filters change
  // (focused/filtered view only - uses the existing id-based endpoint)
  const fetchTemplates = async () => {
    const cronJobTypeId = getCronJobTypeId(selectedCronJobType);
    if (!cronJobTypeId) return;

    try {
      setIsLoadingTemplates(true);
      setError(null);
      const { data } = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/scheduled-cron-jobs/launched-automations`,
        {
          params: {
            page,
            size: PAGE_SIZE,
            status: STATUS,
            versionType,
            cronJobId: cronJobTypeId,
          },
        },
      );
      setTemplates(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      console.error("Failed to fetch launched automations", err);
      setError("Failed to load launched automations.");
      setTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (mode === "filtered") {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCronJobType, versionType, page, mode]);

  // Browse-all (bulk) fetch — categories, cron job types and a page of their scheduled
  // cron jobs, all in one call. Used for the default view + infinite scroll.
  const fetchAllForModule = useCallback(
    async (moduleId, pageToFetch, { append = false } = {}) => {
      if (!moduleId) return;
      try {
        if (append) setIsLoadingMoreAll(true);
        else setIsLoadingAll(true);
        setError(null);

        const { data } = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/scheduled-cron-jobs/module/${moduleId}`,
          {
            params: {
              page: pageToFetch,
              size: PAGE_SIZE_ALL,
              status: STATUS,
              versionType,
            },
          },
        );
        const rawCategories = Array.isArray(data) ? data : data?.data || [];

        setAllCategories((prev) => {
          if (!append) return rawCategories;

          return rawCategories.map((newCat) => {
            const prevCat = prev.find(
              (c) => getCategoryId(c) === getCategoryId(newCat),
            );
            if (!prevCat) return newCat;

            return {
              ...newCat,
              cronJobs: (newCat.cronJobs || []).map((newType) => {
                const prevType = (prevCat.cronJobs || []).find(
                  (t) => getCronJobTypeId(t) === getCronJobTypeId(newType),
                );
                if (!prevType) return newType;

                return {
                  ...newType,
                  scheduledCronJobs: [
                    ...(prevType.scheduledCronJobs || []),
                    ...(newType.scheduledCronJobs || []),
                  ],
                };
              }),
            };
          });
        });

        setAllPage(pageToFetch);

        const stillHasMore = rawCategories.some((cat) =>
          (cat.cronJobs || []).some((type) => {
            const currentPage = type.page ?? pageToFetch;
            const totalPages = type.totalPages ?? currentPage + 1;
            return currentPage < totalPages - 1;
          }),
        );
        setAllHasMore(stillHasMore);
      } catch (err) {
        console.error("Failed to fetch module overview", err);
        setError("Failed to load templates.");
        if (!append) setAllCategories([]);
        setAllHasMore(false);
      } finally {
        if (append) setIsLoadingMoreAll(false);
        else setIsLoadingAll(false);
      }
    },
    [versionType],
  );

  // Default/refresh the browse-all view whenever the module changes, we switch back
  // into "all" mode, or the version filter changes while in "all" mode.
  useEffect(() => {
    if (mode === "all" && selectedModule) {
      const moduleId = selectedModule.moduleId ?? selectedModule.id;
      setAllCategories([]);
      setAllPage(0);
      setAllHasMore(true);
      fetchAllForModule(moduleId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModule, versionType, mode]);

  // Infinite scroll: load the next page (10 more) once the sentinel comes into view.
  useEffect(() => {
    if (mode !== "all" || !selectedModule) return undefined;

    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          allHasMore &&
          !isLoadingAll &&
          !isLoadingMoreAll
        ) {
          const moduleId = selectedModule.moduleId ?? selectedModule.id;
          fetchAllForModule(moduleId, allPage + 1, { append: true });
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    mode,
    selectedModule,
    allHasMore,
    isLoadingAll,
    isLoadingMoreAll,
    allPage,
    fetchAllForModule,
  ]);

  return (
    <div className="automation-explore">
      {/* MODULE MENU */}
      <div className="custom-menu">
        {isLoadingModules ? (
          <div className="custom-menu-item">Loading modules...</div>
        ) : modules.length === 0 ? (
          <div className="custom-menu-item">No modules found.</div>
        ) : (
          modules.map((mod) => {
            const moduleId = mod.moduleId ?? mod.id;
            const isActive =
              (selectedModule?.moduleId ?? selectedModule?.id) === moduleId;
            const hasModuleAccess = canAccessModule(
              mod.moduleAccessId ?? mod.moduleAccessID,
            );

            return (
              <div
                key={moduleId}
                className={`custom-menu-item ${
                  isActive ? "custom-active" : ""
                } ${hasModuleAccess ? "" : "custom-menu-item--locked"}`}
                onClick={() =>
                  hasModuleAccess
                    ? handleModuleClick(mod)
                    : window.location.assign("https://ourdept.com/modules")
                }
                title={
                  hasModuleAccess
                    ? undefined
                    : "You have not purchased this module. Click to view modules."
                }
              >
                {mod.moduleName ?? mod.name}
                {!hasModuleAccess && <Icon icon="mdi:lock-outline" />}
              </div>
            );
          })
        )}
      </div>

      <div className="custom-content">
        {error && <div className="custom-error">{error}</div>}

        {/* CATEGORY INNER MENU (a click here switches to the focused/filtered view) */}
        {selectedModule && (
          <div className="custom-inner-menu">
            {isLoadingCategories ? (
              <div className="custom-inner-menu-item">
                Loading categories...
              </div>
            ) : categories.length === 0 ? (
              <div className="custom-inner-menu-item">
                No categories found for this module.
              </div>
            ) : (
              categories.map((cat) => {
                const categoryId = cat.categoryId ?? cat.id;
                const isActive =
                  (selectedCategory?.categoryId ?? selectedCategory?.id) ===
                  categoryId;

                return (
                  <div
                    key={categoryId}
                    className={`custom-inner-menu-item ${
                      isActive ? "custom-active" : ""
                    }`}
                    onClick={() => handleCategoryClick(cat)}
                  >
                    {cat.categoryName ?? cat.name}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* CRON JOB TYPE MENU (filtered mode only) */}
        {mode === "filtered" && selectedCategory && (
          <div className="custom-type-menu">
            {isLoadingCronJobTypes ? (
              <div className="custom-type-menu-item">
                Loading cron job types...
              </div>
            ) : cronJobTypes.length === 0 ? (
              <div className="custom-type-menu-item">
                No cron job types found for this category.
              </div>
            ) : (
              cronJobTypes.map((type) => {
                const typeId = type.cronJobTypeId ?? type.id;
                const isActive =
                  (selectedCronJobType?.cronJobTypeId ??
                    selectedCronJobType?.id) === typeId;

                return (
                  <div
                    key={typeId}
                    className={`custom-type-menu-item ${
                      isActive ? "custom-active" : ""
                    }`}
                    onClick={() => handleCronJobTypeClick(type)}
                  >
                    {type.cronJobTypeName ?? type.name}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* VERSION TYPE TOGGLE - applies to both the browse-all view and the filtered view */}
        {selectedModule && (
          <div className="version-toggle">
            {VERSION_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`version-toggle__btn ${
                  versionType === opt ? "version-toggle__btn--active" : ""
                }`}
                onClick={() => {
                  setVersionType(opt);
                  setPage(0);
                }}
              >
                {opt === "NEW" ? "New" : "Updated"}
              </button>
            ))}
            {mode === "filtered" && (
              <button
                type="button"
                className="version-toggle__btn browse-all__reset-btn"
                onClick={handleBackToAll}
              >
                &larr; Back to all
              </button>
            )}
          </div>
        )}

        {!selectedModule && !isLoadingModules && (
          <div className="empty-state empty-state--inline">
            <Icon icon="mdi:layers-search-outline" />
            <p>Choose a module to start browsing templates.</p>
          </div>
        )}

        {/* BROWSE-ALL VIEW: every category/cron job type for the module, infinite scroll */}
        {mode === "all" && selectedModule && (
          <div className="browse-all">
            {isLoadingAll ? (
              <div className="empty-state">
                <Icon icon="mdi:loading" className="empty-state__spin" />
                <p>Loading templates...</p>
              </div>
            ) : allCategories.length === 0 ? (
              <div className="empty-state">
                <Icon icon="mdi:creation-outline" />
                <p>No launched automations found</p>
                <span>Try a different version filter or module.</span>
              </div>
            ) : (
              <>
                {allCategories.map((cat) => (
                  <div
                    className="browse-all__category"
                    key={getCategoryId(cat)}
                  >
                    <h3 className="browse-all__category-title">
                      {getCategoryName(cat)}
                    </h3>
                    {(cat.cronJobs || []).map((type) => (
                      <div
                        className="event-type-card"
                        key={getCronJobTypeId(type)}
                      >
                        <div className="event-type-card__header">
                          <h4>{getCronJobTypeName(type)}</h4>
                          <span className="event-type-card__count">
                            {type.totalScheduledCronJobs ??
                              (type.scheduledCronJobs || []).length}{" "}
                            launched
                          </span>
                        </div>
                        {(type.scheduledCronJobs || []).length === 0 ? (
                          <div className="empty-state">
                            <Icon icon="mdi:creation-outline" />
                            <p>No launched automations found</p>
                          </div>
                        ) : (
                          <div
                            className={`scheduled-event-grid scheduled-event-grid--${displayMode}`}
                          >
                            {type.scheduledCronJobs.map((job) => (
                              <ScheduledCronJobCard
                                key={job.scheduledCronJobId}
                                job={job}
                                onReview={(item) =>
                                  handleTemplateAction(item, "edit")
                                }
                                onDownload={(item) =>
                                  handleTemplateAction(item, "download")
                                }
                                onViewUsed={(item) =>
                                  viewUsedTemplate(item, cat, type)
                                }
                                isDownloading={
                                  downloadingId === job.scheduledCronJobId
                                }
                                displayMode={displayMode}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}

                {/* infinite scroll sentinel */}
                <div ref={sentinelRef} className="browse-all__sentinel" />

                {isLoadingMoreAll && (
                  <div className="empty-state empty-state--inline">
                    <Icon icon="mdi:loading" className="empty-state__spin" />
                    <p>Loading more...</p>
                  </div>
                )}
                {!allHasMore && !isLoadingMoreAll && (
                  <div className="browse-all__end">
                    You&apos;ve reached the end.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* FILTERED VIEW: single cron job type, focused grid with Prev/Next pagination */}
        {mode === "filtered" && selectedCronJobType && (
          <div className="event-type-card">
            <div className="event-type-card__header">
              <h3>
                {selectedCronJobType.cronJobTypeName ??
                  selectedCronJobType.name}
              </h3>
              <span className="event-type-card__count">
                {templates.length} launched
              </span>
            </div>

            {isLoadingTemplates ? (
              <div className="empty-state">
                <Icon icon="mdi:loading" className="empty-state__spin" />
                <p>Loading templates...</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="empty-state">
                <Icon icon="mdi:creation-outline" />
                <p>No launched automations found</p>
                <span>Try a different version filter or cron job type.</span>
              </div>
            ) : (
              <div
                className={`scheduled-event-grid scheduled-event-grid--${displayMode}`}
              >
                {templates.map((job) => (
                  <ScheduledCronJobCard
                    key={job.scheduledCronJobId}
                    job={job}
                    onReview={(item) => handleTemplateAction(item, "edit")}
                    onDownload={(item) =>
                      handleTemplateAction(item, "download")
                    }
                    onViewUsed={(item) => viewUsedTemplate(item)}
                    isDownloading={downloadingId === job.scheduledCronJobId}
                    displayMode={displayMode}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* PAGINATION (filtered view only) */}
        {mode === "filtered" && selectedCronJobType && templates.length > 0 && (
          <div className="explore-pagination">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span>Page {page + 1}</span>
            <button
              disabled={templates.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {reviewItem && (
        <LaunchedTemplateReviewForm
          item={reviewItem}
          useSaveEndpoint
          type="cron"
          onClose={() => setReviewItem(null)}
          onSaved={() => {
            setReviewItem(null);
            if (mode === "filtered") {
              fetchTemplates();
            } else if (selectedModule) {
              const moduleId = selectedModule.moduleId ?? selectedModule.id;
              setAllCategories([]);
              setAllPage(0);
              setAllHasMore(true);
              fetchAllForModule(moduleId, 0);
            }
          }}
        />
      )}
    </div>
  );
};

export default AutomationExplore;
